import React from 'react';
import classNames from 'classnames';
import { addHours, format } from 'date-fns';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import { FaPlus } from 'react-icons/fa';
import { formatCurrency } from '../../../../../../../utils/formatCurrency.js';

/**
 * @typedef {object} RecebivelListDialogProps
 * @property {boolean} visible
 * @property {{usuario_nome?: string, contratante_id?: string|number, credor_id?: string|number}} itemInfo
 * @property {boolean} isLoading
 * @property {Record<string, any>[]} listaRecebivelTipos
 * @property {() => void} onHide
 * @property {() => void} onShow
 * @property {() => void} onOpenCreate
 * @property {(rowData: Record<string, any>) => void} onSelectRow
 * @property {(dadosAnteriores: Record<string, any>|null, dadosNovos: Record<string, any>|null) => string} formatarAlteracoes
 */

/**
 * Dialog principal de listagem e gerenciamento de parcelamentos.
 *
 * @param {RecebivelListDialogProps} props
 * @returns {JSX.Element}
 */
export default function RecebivelListDialog(props) {
    const {
        visible,
        itemInfo,
        isLoading,
        listaRecebivelTipos,
        onHide,
        onShow,
        onOpenCreate,
        onSelectRow,
        formatarAlteracoes,
    } = props;

    return (
        <Dialog
            header={`Gerenciar Parcelamentos ${itemInfo.usuario_nome ? ` - "${itemInfo.usuario_nome}"` : ''}`}
            onHide={onHide}
            onShow={onShow}
            visible={visible}
            pt={{
                root: 'w-screen md:w-[70vw] max-h-[100vh]',
                header: classNames('flex items-center justify-between shrink-0',
                    'bg-white text-gray-800 border-t-0  rounded-tl-lg rounded-tr-lg p-6',
                    'dark:bg-gray-900  dark:text-white/80'),
                content: ({ state }) => ({
                    className: classNames('overflow-auto w-full max-h-[85vh]',
                        'bg-white text-gray-700 px-6 pb-8 pt-0',
                        'rounded-bl-lg rounded-br-lg',
                        'dark:bg-gray-900 dark:text-white/80 ', {
                        'grow': state.maximized
                    })
                }),
            }}
        >
            <div className='w-full'>
                <DataTable
                    loading={isLoading}
                    value={listaRecebivelTipos}
                    header={(
                        <div className='flex justify-between'>
                            <div>
                                <h5>Cadastrar novo parcelamento</h5>
                            </div>
                            <div className='flex'>
                                <div>
                                    <Button icon={<FaPlus />} severity="success" type="button" onClick={onOpenCreate} />
                                </div>
                            </div>
                        </div>
                    )}
                    selectionMode="single"
                    emptyMessage="Nenhum parcelamento cadastrado!"
                    dataKey='id'
                    onSelectionChange={(e) => {
                        onSelectRow(e.value);
                    }}
                >
                    <Column
                        header={(<div><p>Descrição</p></div>)}
                        body={rowData => rowData.recebivel_tipo_descricao}
                        field="recebivel_tipo_descricao"
                        style={{ fontSize: 15, fontWeight: 'bold' }}
                    />
                    <Column
                        header={(<div><p>Valor</p></div>)}
                        body={rowData => formatCurrency(rowData.valor)}
                        field="recebivel_tipo_valor"
                        style={{ fontSize: 15, fontWeight: 'bold' }}
                    />
                    <Column
                        header={(<div><p>Desconto</p></div>)}
                        body={rowData => rowData.desconto ? ` ${rowData.desconto} (${rowData.desconto_tipo})` : ''}
                        field="recebivel_tipo_desconto"
                        style={{ fontSize: 15, fontWeight: 'bold' }}
                    />
                    <Column
                        header={(<div><p>Vencimento</p></div>)}
                        body={rowData => rowData.vencimento ? `${rowData.vencimento}ª dia do mês` : ''}
                        field="recebivel_vencimento"
                        style={{ fontSize: 15, fontWeight: 'bold' }}
                    />
                    <Column
                        header={(<div><p>Primeiro Vencimento</p></div>)}
                        body={rowData => {
                            if (rowData.rec_contratante_inicio) {
                                return format(addHours(new Date(rowData.rec_contratante_inicio), 3), 'dd/MM/yyyy');
                            } else return '';
                        }}
                        field="recebivel_tipo_data_inicio"
                        style={{ fontSize: 15, fontWeight: 'bold' }}
                    />
                    <Column
                        header={(<div><p>Último Vencimento</p></div>)}
                        body={rowData => {
                            if (rowData.rec_contratante_fim) {
                                return format(addHours(new Date(rowData.rec_contratante_fim), 3), 'dd/MM/yyyy');
                            } else return '';
                        }}
                        field="recebivel_tipo_data_fim"
                        style={{ fontSize: 15, fontWeight: 'bold' }}
                    />
                    <Column
                        header={(<div><p>Última Atualização</p></div>)}
                        body={rowData => {
                            if (rowData.updated_at) {
                                const tipoId = rowData.con_recebivel_tipo_id.split('-', 1)[0];
                                const targetId = `ultimaAtualizacao-${tipoId.replace(/-/g, '\\-')}`;

                                return (
                                    <div>
                                        <Tooltip target={`.${targetId}`}
                                            content={formatarAlteracoes(rowData.dados_anteriores, rowData.dados_novos)}
                                            position="top" />
                                        <span className={targetId}>
                                            {format(addHours(new Date(rowData.updated_at), -3), 'dd/MM/yyyy HH:ii')}
                                        </span>
                                    </div>
                                );
                            } else return '';
                        }}
                        field="ultima_atualizacao"
                        style={{ fontSize: 15, fontWeight: 'bold' }}
                    />
                </DataTable>
            </div>
        </Dialog>
    );
}
